import * as React from "react";
import {
	Autocomplete, Backdrop, Box, Button, Card, CardContent, CardHeader, CircularProgress, Snackbar,
	Container, createFilterOptions, Dialog, DialogActions, DialogContent, DialogContentText, Alert,
	DialogTitle, IconButton, Paper, Slide, Stack, TextField, Typography, useMediaQuery, useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteIcon from "@mui/icons-material/Delete";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {ThemeProvider} from "@mui/material/styles";
import {useNavigate, useLocation} from "react-router-dom";
import {
	useGetDepartmentsQuery,
	useAddDepartmentMutation,
	useDeleteCatalogEntityMutation,
} from "../../store/catalogApi";
import {buttonTheme} from "../../theme";
import { getVendorId } from "../../utils/authSession";

/* ---------------- helpers ---------------- */
const filter = createFilterOptions();

const blue = {
	main: "#2563eb",
	light: "#e0edff",
	dark: "#1e4ed8",
};

const green = {
	main: "#059669",
	light: "#d1fae5",
	dark: "#047857",
};

const suggested = [
	"Electronics",
	"Clothing",
	"Home Appliances",
	"Large Appliances",
	"Kitchen Appliances",
	"Machinery",
	"Furniture",
	"Tools and Hardware",
	"Sports",
	"Books",
	"Groceries",
	"Automotive Accessories",
	"Pet Supplies",
	"Office Supplies",
	"Exercise and Fitness",
	"Toys",
].map((d) => ({departmentName: d}));

const Transition = React.forwardRef(function Transition(props, ref) {
	return <Slide direction="up" ref={ref} {...props} />;
});

/* ============================================================================= */
export default function ManageDepartments() {
	const theme = useTheme();
	const navigate = useNavigate();
	const location = useLocation();
	const isMobile = useMediaQuery("(max-width:750px)");

	const vendorId = getVendorId();

	const {data: posts = [], isLoading, isFetching, isError} = useGetDepartmentsQuery(vendorId, {skip: !vendorId});
	const [addDepartment, {isLoading: adding}] = useAddDepartmentMutation();
	const [deleteEntity] = useDeleteCatalogEntityMutation();
	const loading = isLoading || isFetching || adding;

	const [dialogOpen, setDialogOpen] = React.useState(false);
	const [value, setValue] = React.useState(null);

	const [confirmOpen, setConfirmOpen] = React.useState(false);
	const [deptToDelete, setDeptToDelete] = React.useState(null);
	const [deleteLoading, setDeleteLoading] = React.useState(false);


	const [snack, setSnack] = React.useState({
		open: false,
		msg: "",
		severity: "success",
	});

	const handleSnackClose = () => setSnack((s) => ({...s, open: false}));

	React.useEffect(() => {
		if (isError) {
			setSnack({open: true, msg: "Could not load departments.", severity: "error"});
		}
	}, [isError]);

	// show confirm dialog
	const handleDeleteClick = (dept) => {
		setDeptToDelete(dept);
		setConfirmOpen(true);
	};

// confirm button in dialog
	const confirmDelete = async () => {
		if (!deptToDelete) return;
		setDeleteLoading(true);

		try {
			const data = await deleteEntity({
				entityType: "department",
				vendorId,
				id: deptToDelete.id,
			}).unwrap();

			if (data.canDelete) {
				setSnack({open: true, msg: "Department deleted.", severity: "success"});
			} else {
				setSnack({
					open: true,
					msg:
						"Cannot delete. There are categories, sub‑categories or products under this department.",
					severity: "error",
				});
			}
		} catch (err) {
			setSnack({
				open: true,
				msg: "Something went wrong. Please try again.",
				severity: "error",
			});
		}

		setDeleteLoading(false);
		setConfirmOpen(false);
		setDeptToDelete(null);
	};


	React.useEffect(() => {
		localStorage.setItem("visitedMd", "true");
		// stale caches from the old reducer-based flow
		localStorage.removeItem("DepartmentData");
	}, []);

	/* -------- handlers -------- */
	const handleAdd = async () => {
		if (!value || !value.departmentName) return;
		const exists = posts.some(
			(p) => p.departmentName.toLowerCase() === value.departmentName.toLowerCase()
		);
		if (exists) return alert("Department already added");

		try {
			await addDepartment({departmentName: value.departmentName, vendorId}).unwrap();
			setValue(null);
			setDialogOpen(false);
		} catch (err) {
			setSnack({open: true, msg: "Could not add department. Please try again.", severity: "error"});
		}
	};

	return (
		<Box sx={{bgcolor: "#f8fafc", minHeight: "100vh", py: 4}}>
			<ThemeProvider theme={theme}>
				<Container maxWidth="md">
					<Box sx={{mb: 4, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2}}>
						<Button
							startIcon={<ArrowBackIosNewIcon/>}
							variant="outlined" color="secondary"
							sx={{
								borderRadius: 2, textTransform: "none",
								fontWeight: 600, bgcolor: "#fff"
							}}
							onClick={() =>
								navigate(location.state?.fromManageProducts ? "/mp" : "/home")
							}
						>
							Back
						</Button>

						<Typography variant="h6" sx={{fontSize: '1rem'}} fontWeight={700}>
							Manage Departments
						</Typography>

						<Button
							variant="contained" color="primary"
							sx={{
								color:'#000',
								borderRadius: 2, textTransform: "none",
								fontWeight: 600, minWidth: 72
							}}
							onClick={() =>
								navigate(location.state?.fromManageProducts ? "/mp" : "/home")
							}
						>
							Done
						</Button>
					</Box>

					{/* ---------- card ---------- */}
					<Card
						sx={{
							borderRadius: 3, border: "1px solid #e5e7eb",
							boxShadow: "0 3px 10px rgba(0,0,0,0.04)"
						}}
					>
						<CardHeader
							title={<Typography fontWeight={700}>Store Departments</Typography>}
							subheader={
								<Typography variant="body2" color="text.secondary">
									Organize your products into departments for better management
								</Typography>
							}
							action={
								<Button
									startIcon={<AddIcon/>}
									variant="contained"
									color="primary"
									sx={{
										color:'#000',
										borderRadius: 2, textTransform: "none", fontWeight: 600
									}}
									onClick={() => setDialogOpen(true)}
								>
									Add Department
								</Button>
							}
							sx={{pb: 0, "& .MuiCardHeader-action": {alignSelf: "center"}}}
						/>

						<CardContent>
							{/* empty state */}
							{posts.length === 0 && (
								<Box sx={{textAlign: "center", py: 10}}>
									<ErrorOutlineIcon sx={{fontSize: 64, mb: 2, color: "#cbd5e1"}}/>
									<Typography variant="h6" mb={1}>
										No departments yet
									</Typography>
									<Typography variant="body2" color="text.secondary" mb={3}>
										Get started by adding your first department.
									</Typography>
									<Button
										startIcon={<AddIcon/>}
										variant="contained"
										color="primary"
										sx={{
											borderRadius: 2, textTransform: "none", fontWeight: 600
										}}
										onClick={() => setDialogOpen(true)}
									>
										Add Your First Department
									</Button>
								</Box>
							)}

							{/* list */}
							{posts.length > 0 && (
								<Stack spacing={2}>
									{posts.map((dept) => (
										<Paper
											key={dept.id ?? dept.departmentName}
											variant="outlined"
											sx={{
												p: 2, borderRadius: 2, display: "flex",
												alignItems: "center", justifyContent: "space-between",
												borderColor: "#e5e7eb", transition: "all 0.15s",
												"&:hover": {borderColor: "#d1d5db", bgcolor: "#f9fafb"},
												'&:hover .row-actions': {opacity: 1}
											}}
										>
											{/* left section */}
											<Box sx={{display: "flex", alignItems: "center", flex: 1, cursor: "pointer"}}
											     onClick={() =>
												     navigate("/sc", {
													     state: {
														     id: dept.id,
														     vendorId: dept.vendorId,
														     departmentName: dept.departmentName,
													     },
												     })
											     }
											>
												<Box
													sx={{
														width: 40, height: 40, mr: 2,
														borderRadius: 2, bgcolor: blue.light,
														display: "flex", alignItems: "center",
														justifyContent: "center"
													}}
												>
													<ApartmentRoundedIcon sx={{color: blue.main}}/>
												</Box>
												<Box>
													<Typography fontWeight={600}>{dept.departmentName}</Typography>
													<Typography
														variant="body2"
														color="text.secondary"
														sx={{fontSize: "0.8125rem"}}
													>
														Click to manage categories
													</Typography>
												</Box>
											</Box>

											{/* right actions - hidden until hover */}
											<Box
												className="row-actions"
												sx={{
													ml: 1, display: "flex", alignItems: "center",
													opacity: 0, transition: "opacity .15s"
												}}
											>
												<IconButton
													size="small"
													onClick={() => handleDeleteClick(dept)}
													sx={{color: "#ef4444", "&:hover": {bgcolor: "#fee2e2"}}}
												>
													<DeleteIcon fontSize="small"/>
												</IconButton>

												<IconButton
													size="small"
													onClick={() => {

														navigate("/sc", {
															state: {
																id: dept.id,
																vendorId: dept.vendorId,
																departmentName: dept.departmentName,
															},
														});
													}}
													sx={{color: "#64748b", "&:hover": {bgcolor: "#f1f5f9"}}}
												>
													<ChevronRightIcon fontSize="small"/>
												</IconButton>
											</Box>
										</Paper>
									))}
								</Stack>
							)}
						</CardContent>
					</Card>

					{/* centred footer text */}
					{posts.length > 0 && (
						<Box sx={{textAlign: "center", mt: 2}}>
							<Typography variant="caption" color="text.secondary">
								{posts.length} {posts.length === 1 ? "department" : "departments"} configured
							</Typography>
						</Box>
					)}
				</Container>
				<Dialog
					open={confirmOpen}
					onClose={() => setConfirmOpen(false)}
					TransitionComponent={Transition}
					fullWidth
					maxWidth="xs"
					PaperProps={{sx: {borderRadius: 3}}}
				>
					<DialogTitle fontWeight={700}>Delete Department?</DialogTitle>

					<DialogContent dividers sx={{pt: 1.5}}>
						<DialogContentText>
							Deleting <strong>{deptToDelete?.departmentName}</strong> will remove the
							department permanently if it has no child items. This action cannot be
							undone. Continue?
						</DialogContentText>
					</DialogContent>

					<DialogActions sx={{px: 3, py: 2}}>
						<Button color='secondary' onClick={() => setConfirmOpen(false)} sx={{textTransform: "none"}}>
							Cancel
						</Button>
						<Button
							variant="contained"
							onClick={confirmDelete}
							disabled={deleteLoading}
							color="error"
							sx={{
								textTransform: "none",
								fontWeight: 600,
							}}
						>
							{deleteLoading ? <CircularProgress size={20}/> : "Delete"}
						</Button>
					</DialogActions>
				</Dialog>
				{/* ---------------- dialog ---------------- */}
				<Dialog
					open={dialogOpen}
					onClose={() => setDialogOpen(false)}
					TransitionComponent={Transition}
					fullWidth
					maxWidth="sm"
					PaperProps={{sx: {borderRadius: 3}}}
				>
					<DialogTitle fontWeight={700}>Add New Department</DialogTitle>
					<DialogContent dividers sx={{pt: 1.5}}>
						<DialogContentText mb={3}>
							Enter the name of the department. Select an option from the list or add a new one.
						</DialogContentText>

						{/* ---- fixed Autocomplete ---- */}
						<Autocomplete
							value={value}
							onChange={(e, newValue) => {
								if (typeof newValue === "string") {
									setValue({departmentName: newValue});
								} else if (newValue && newValue.inputValue) {
									setValue({departmentName: newValue.inputValue});
								} else {
									setValue(newValue);
								}
							}}
							filterOptions={(options, params) => {
								const filtered = filter(options, params);
								const {inputValue} = params;
								const isExisting = options.some(
									(o) =>
										o.departmentName.toLowerCase() === inputValue.toLowerCase()
								);
								if (inputValue !== "" && !isExisting) {
									filtered.push({
										inputValue,
										departmentName: `Add "${inputValue}"`,
									});
								}
								return filtered;
							}}
							options={[...posts, ...suggested]}
							getOptionLabel={(option) =>
								typeof option === "string"
									? option
									: option.inputValue
										? option.inputValue
										: option.departmentName
							}
							renderOption={(props, option) => (
								<Box component="li" {...props}>
									{option.departmentName}
								</Box>
							)}
							selectOnFocus
							clearOnBlur
							handleHomeEndKeys
							freeSolo
							renderInput={(params) => (
								<TextField {...params} label="Department Name" fullWidth/>
							)}
						/>
					</DialogContent>
					<DialogActions sx={{px: 3, py: 2}}>
						<Button color='secondary' onClick={() => setDialogOpen(false)} sx={{textTransform: "none"}}>
							Cancel
						</Button>
						<Button
							variant="contained"
							onClick={handleAdd}
							disabled={!value || !value.departmentName}
							color="primary"
							sx={{
								color:'#000',
								borderRadius: 2,
								textTransform: "none",
								fontWeight: 600,
							}}
						>
							Add Department
						</Button>
					</DialogActions>
				</Dialog>

				{/* loader */}
				<Backdrop sx={{color: "#fff", zIndex: theme.zIndex.drawer + 1}} open={loading}>
					<CircularProgress color="inherit"/>
				</Backdrop>
				<Snackbar
					open={snack.open}
					autoHideDuration={4000}
					onClose={handleSnackClose}
					anchorOrigin={{vertical: "bottom", horizontal: "center"}}
				>
					<Alert
						onClose={handleSnackClose}
						severity={snack.severity}
						sx={{width: "100%"}}
					>
						{snack.msg}
					</Alert>
				</Snackbar>
			</ThemeProvider>
		</Box>
	);
}
